// Service, route and airport landing pages (search and ads). One layout; each page's copy, prices,
// FAQ and related links come from its entry in services.js, cityRoutes.js or ../airports.js.
// Prices come from prices.json via prices.js, never from the copy. Titles and descriptions are in
// pageMeta.json, and scripts/prerender.js saves the rendered page into build/<path>/index.html.
import React from 'react';
import { Link } from 'react-router-dom';
import { PiArrowRight, PiCheckCircle, PiInfo, PiPhone, PiPlus, PiSuitcaseRolling, PiUsers } from 'react-icons/pi';
import { Button, Card, Container, Eyebrow, SectionHeading } from '../../ui';
import InfoPage, { PageHero, PHONE_DISPLAY, PHONE_HREF } from '../InfoPage';
import { HALF_WIDTH, siteImage } from '../../images';
import { PAGE_META } from '../../hooks';
import pageMeta from '../../pageMeta.json';
import { FETCHED_AT, VEHICLES, linkPrice, money } from './prices';
import { LANDING_BY_PATH } from './index';
import { airportFor, bookFromAirport, bookToAirport } from '../../../components/BookingFlow/airports';
import './landing.css';

const FLEET = [
  ['sedan', '/images/vehicles/fleet-sedan.webp', 'Cadillac Lyriq in black, side view – RoolDrive Luxury Sedan'],
  ['suv', '/images/vehicles/fleet-suv.webp', 'Cadillac Escalade ESV in black, side view – RoolDrive Luxury SUV'],
];

const checked = new Date(`${FETCHED_AT}T12:00:00Z`).toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York',
});

function PriceTable({ caption, rows, firstColumn }) {
  return (
    <div className="lp-table-wrap">
      <table className="lp-table">
        <caption className="lx-sr-only">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">{firstColumn}</th>
            <th scope="col">{VEHICLES.sedan.name}</th>
            <th scope="col">{VEHICLES.suv.name}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.label}</th>
              <td>{money(r.sedan)}</td>
              <td>{money(r.suv)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JsonLd({ data }) {
  // Static data from our own page entries; < is escaped so the copy can never close the tag.
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }} />;
}

function schemaFor(page, url) {
  const offers = page.schema.offers || [
    { name: VEHICLES.sedan.name, price: page.from.sedan },
    { name: VEHICLES.suv.name, price: page.from.suv },
  ];
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: page.schema.name,
      serviceType: page.schema.serviceType,
      description: PAGE_META[page.path]?.description,
      url,
      image: `${pageMeta.siteUrl}/images/site/${page.heroImage}.webp`,
      areaServed: page.schema.areaServed.map((name) => ({ '@type': 'Place', name })),
      provider: {
        '@type': 'LocalBusiness',
        name: 'RoolDrive',
        url: `${pageMeta.siteUrl}/`,
        telephone: '+1-929-231-5951',
      },
      offers: offers.map((o) => ({
        '@type': 'Offer',
        name: o.name,
        url,
        priceCurrency: 'USD',
        ...(o.unit
          ? { priceSpecification: { '@type': 'UnitPriceSpecification', price: Math.round(o.price).toFixed(2), priceCurrency: 'USD', unitText: o.unit } }
          : { price: Math.round(o.price).toFixed(2) }),
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ];
}

export default function LandingPage({ page }) {
  const meta = PAGE_META[page.path] || {};
  const url = `${pageMeta.siteUrl}${page.path}`;
  const related = page.related.map((p) => LANDING_BY_PATH[p]).filter(Boolean);
  // Flat-rate rows (airport <-> Manhattan) are fixed prices, not estimates
  const hasFlat = page.priceTables.some((t) => t.rows.some((r) => r.flatRate));
  const allFlat = page.priceTables.every((t) => t.rows.every((r) => r.flatRate));
  return (
    <InfoPage
      title={meta.title}
      description={meta.description}
      hero={(
        <PageHero image={page.heroImage} eyebrow={page.eyebrow} title={page.title} lead={page.lead}>
          <p className="lp-from">
            From <strong>{money(Math.min(page.from.sedan, page.from.suv))}</strong>
            {page.fromNote && ` ${page.fromNote}`} · Sedan or SUV · {allFlat ? 'fixed price, tolls included' : 'estimated, exact price shown before you pay'}
          </p>
          <div className="pg-actions">
            {airportFor(page.airport) ? (
              <>
                <Button to={bookFromAirport(page.airport)} size="lg" iconAfter={PiArrowRight}>Arriving at {airportFor(page.airport).name}</Button>
                <Button to={bookToAirport(page.airport)} size="lg" iconAfter={PiArrowRight}>Flying from {airportFor(page.airport).name}</Button>
              </>
            ) : (
              <Button to={page.book} size="lg" iconAfter={PiArrowRight}>See your exact price</Button>
            )}
            <Button href={PHONE_HREF} variant="secondary" size="lg" icon={PiPhone}>{PHONE_DISPLAY}</Button>
          </div>
        </PageHero>
      )}
    >
      {schemaFor(page, url).map((data) => <JsonLd key={data['@type']} data={data} />)}

      <section className="lx-section">
        <Container>
          <div className="lp-intro">
            <div className="lx-stack" style={{ gap: 20 }}>
              <SectionHeading eyebrow={page.introEyebrow} title={page.introTitle} />
              {page.intro.map((p) => <p key={p.slice(0, 40)} className="pg-text lp-prose">{p}</p>)}
            </div>
            {page.facts && (
              <dl className="lp-facts">
                {page.facts.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </Container>
      </section>

      <section className="lx-section" style={{ paddingTop: 0 }} id="prices">
        <Container>
          <SectionHeading eyebrow="Prices" title={page.priceTitle} lead={page.priceLead} />
          {page.priceTables.map((t) => (
            <div key={t.caption} className="lp-price-block">
              {page.priceTables.length > 1 && <h3 className="lx-h3">{t.caption}</h3>}
              <PriceTable caption={t.caption} rows={t.rows} firstColumn={t.firstColumn || 'Trip'} />
            </div>
          ))}
          <p className="lp-note">
            <PiInfo aria-hidden />
            <span>
              {hasFlat ? (
                <><strong>Flat rates between the airports and Manhattan are fixed</strong> and don’t change with the time of day
                or traffic. All other prices on this page are estimates and can be different when you book, </>
              ) : (
                <><strong>All prices on this page are estimates</strong> and can be different when you book, </>
              )}
              depending on your exact pickup and drop-off locations, distance, route, traffic and time. Prices include
              tolls. {page.priceNote} Prices are the totals our booking system quoted on {checked}, rounded to the nearest
              dollar, before any optional tip. Your exact price is always shown before you pay.
            </span>
          </p>
          <div className="lp-fleet">
            {FLEET.map(([key, src, alt]) => (
              <Card key={key} className="lp-fleet-card">
                <img src={src} alt={alt} width="960" height="480" loading="lazy" />
                <div>
                  <h3 className="lx-h3">{VEHICLES[key].name}</h3>
                  <p className="lp-fleet-meta">
                    <span><PiUsers aria-hidden /> Up to {VEHICLES[key].passengers} passengers</span>
                    <span><PiSuitcaseRolling aria-hidden /> {VEHICLES[key].luggage} large bags</span>
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {page.sections.map((s, i) => (
        <section key={s.title} className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <div className={`pg-split${i % 2 ? ' lp-split--flip' : ''}`}>
              <div className="lx-stack" style={{ gap: 20 }}>
                <SectionHeading eyebrow={s.eyebrow} title={s.title} />
                {s.paragraphs.map((p) => <p key={p.slice(0, 40)} className="pg-text lp-prose">{p}</p>)}
                {s.list && (
                  <ul className="lp-checks">
                    {s.list.map((item) => <li key={item}><PiCheckCircle aria-hidden />{item}</li>)}
                  </ul>
                )}
              </div>
              {s.image && (
                <div className="pg-split-media">
                  <img {...siteImage(s.image, HALF_WIDTH)} alt={s.imageAlt} loading="lazy" />
                </div>
              )}
            </div>
          </Container>
        </section>
      ))}

      <section className="lx-section" style={{ paddingTop: 0 }}>
        <Container>
          <div className="lp-included">
            <Card>
              <Eyebrow>Included in the price</Eyebrow>
              <ul className="lp-checks">
                {page.included.map((item) => <li key={item}><PiCheckCircle aria-hidden />{item}</li>)}
              </ul>
            </Card>
            <Card>
              <Eyebrow>Charged only if it happens</Eyebrow>
              <ul className="lp-checks lp-checks--muted">
                {page.extras.map((item) => <li key={item}><PiInfo aria-hidden />{item}</li>)}
              </ul>
              <p className="lp-small">Full details in our <Link to="/terms">Terms of service</Link>.</p>
            </Card>
          </div>
        </Container>
      </section>

      <section className="lx-section" style={{ paddingTop: 0 }}>
        <Container>
          <div className="pg-faq-group" style={{ maxWidth: 820 }}>
            <h2 className="lx-h2">{page.faqTitle}</h2>
            {page.faq.map(({ q, a }) => (
              <details key={q} className="pg-faq">
                <summary>{q}<PiPlus aria-hidden /></summary>
                <p>{a}</p>
              </details>
            ))}
            <p className="pg-text" style={{ marginTop: 20 }}>
              More answers in our <Link to="/help">help center</Link>, or call us 24/7 on <a href={PHONE_HREF}>{PHONE_DISPLAY}</a>.
            </p>
            <div className="pg-actions" style={{ marginTop: 24 }}>
              <Button to={page.book} iconAfter={PiArrowRight}>{page.cta}</Button>
            </div>
          </div>
        </Container>
      </section>

      {related.length > 0 && (
        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <SectionHeading eyebrow="Also popular" title={<>Related <em>rides</em></>} />
            <ul className="lp-related">
              {related.map((r) => (
                <li key={r.path}>
                  <Link to={r.path} className="lp-related-link">
                    <span>
                      <strong>{r.linkText}</strong>
                      <span className="lx-muted">{linkPrice(r)}</span>
                    </span>
                    <PiArrowRight aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </Container>
        </section>
      )}
    </InfoPage>
  );
}
